import {FC, MouseEvent, useState} from 'react';
import {useCan} from 'src/permissions/hooks';
import {BadgeOutlined, ContentCopyOutlined, PersonOutlineOutlined} from '@mui/icons-material';
import {Box, Button, IconButton, Popover, Typography} from '@mui/material';
import {useIntl} from 'react-intl';
import copy from 'copy-to-clipboard';
import {useScreenTier} from 'src/hooks/useScreenTier';
import {ActionSheet, ActionSheetDestructive} from 'src/components/mobile/ActionSheet';
import {PhoneCardFacts} from 'src/components/mobile/PhoneCardFacts';
import {PhoneCardHead} from 'src/components/mobile/PhoneCardHead';
import {phonePillsRowSx, phoneTitleCellSx} from 'src/components/mobile/mobileDesign.sx';
import {NotifySuccess} from '../../../../utils/notification';
import {usersApi} from '../../../../api';
import {useAppDispatch} from '../../../../app/hooks';
import {preloaderChangeStatus} from '../../../../stores/common/commonSlice';
import {AppDispatch, BaseUserInfo} from '../../../../types';
import {ColorField} from 'src/components/common/ColorField';
import {UserCell} from 'src/components/users/UserCell';
import {TableCard} from '../../../tables/respnsiveTable/TableCard';
import {MenuButton} from '../../../tables/respnsiveTable/MenuButton';
import dayjs from 'dayjs';
import {
    iconBtnSx,
    idValueSx,
    menuBtnSx,
    menuBtnTextSx,
    popoverSx,
} from './TechnicalTeamItemCard.sx';

type DeleteUserMutationTrigger = ReturnType<typeof usersApi.useDeleteUserMutation>[0];

const handleDeleteUser =
    (trigger: DeleteUserMutationTrigger, dispatch: AppDispatch, userId: number, businessId: number) =>
        async () => {
            try {
                dispatch(preloaderChangeStatus(true));
                await trigger({userId, businessId})
                    .unwrap()
                    .finally(() => {
                        dispatch(preloaderChangeStatus(false));
                    });
            } catch (error) {
                console.error(error);
            } finally {
                dispatch(preloaderChangeStatus(false));
            }
        };

export const TechnicalTeamItemCard: FC<BaseUserInfo> = (member) => {
    const {
        id,
        userType,
        firstName,
        lastName,
        lastLogin,
        emailAddress,
        business,
        created,
    } = member;
    // The menu exists only to offer this action, so the permission decides whether it appears.
    // Where a flag is also passed in, both must hold: the parent says whether the action belongs
    // on this screen, the permission says whether this person may perform it - and the prop
    // defaults to true, so on its own it fails open.
    const canDeleteUser = useCan('users.deleteAccount');
    const intl = useIntl();
    const [trigger] = usersApi.useDeleteUserMutation();
    const dispatch = useAppDispatch();
    // Below a desktop the menu is a sheet; from md up it stays the popover it has always been.
    const {isCompact} = useScreenTier();
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const copyToClipboard = () => {
        copy(String(id), {debug: true});
        NotifySuccess(intl.formatMessage({id: 'copiedSuccesfully'}));
    };

    const displayName = `${firstName} ${lastName}`.trim() || emailAddress || '';

    // One node, drawn by whichever of the two the width calls for: the handler and the gate around
    // it are the same in both.
    const deleteAction = business ? (
        <Button
            sx={menuBtnSx}
            onClick={handleDeleteUser(trigger, dispatch, +id, +business.id)}
        >
            <Typography sx={menuBtnTextSx}>{intl.formatMessage({id: 'delete'})}</Typography>
        </Button>
    ) : null;

    // The menu the card's head carries. The sheet and the popover are portalled, so sitting in the
    // head's row costs the layout nothing.
    const menuAction = business ? (
        <>
            {canDeleteUser && <MenuButton handleClick={handleClick}/>}
            {isCompact ? (
                <ActionSheet
                    open={open}
                    onClose={handleClose}
                    icon={<BadgeOutlined/>}
                    title={displayName}
                    subtitle={emailAddress}
                >
                    <ActionSheetDestructive>{deleteAction}</ActionSheetDestructive>
                </ActionSheet>
            ) : (
                <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    sx={popoverSx}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                >
                    {deleteAction}
                </Popover>
            )}
        </>
    ) : undefined;

    return (
        <TableCard>
            <PhoneCardHead
                icon={<PersonOutlineOutlined/>}
                person
                title={(
                    // The name is the door, and the door is this list's own name cell - the same
                    // component the grid renders (TechnicalTeamTable.tableConfig.tsx), which
                    // sends a manager to the team page rather than to the user page.
                    <Box sx={phoneTitleCellSx}>
                        <UserCell data={member}/>
                    </Box>
                )}
                subtitle={emailAddress}
                action={menuAction}
            />
            {userType && (
                <Box sx={phonePillsRowSx}>
                    <ColorField label={userType}/>
                </Box>
            )}
            <PhoneCardFacts
                facts={[
                    {
                        label: intl.formatMessage({id: 'userId'}),
                        value: (
                            <Box
                                component="span"
                                sx={idValueSx}
                            >
                                {/* Plain text, not a link. It used to open /app/users/:id, which
                                    is not where this list's rows go; the title is now the card's
                                    one door, and a second link here could only disagree with it. */}
                                {id}
                                <IconButton
                                    onClick={copyToClipboard}
                                    sx={iconBtnSx}
                                >
                                    <ContentCopyOutlined sx={{width: '0.75rem'}}/>
                                </IconButton>
                            </Box>
                        ),
                    },
                    {
                        // The card used one "DATE" label for both dates, which on two facts side by
                        // side says nothing. These are the ids this list's own desktop columns use
                        // for the very same two values - no new key, and the facts now differ.
                        label: intl.formatMessage({id: 'createdDateColumnHeader'}),
                        value: dayjs(created).format('DD/MM/YY'),
                    },
                    {
                        label: intl.formatMessage({id: 'lastLoginColumnHeader'}),
                        value: dayjs(lastLogin).format('DD/MM/YY'),
                    },
                ]}
            />
        </TableCard>
    );
};
